<template>
  <div class="tmap">
    <div ref="mapContainer" class="tian-di-tu-map"></div>
    <div class="search" v-if="enableSearch">
      <el-input
        v-model="searchText"
        style="max-width: 300px"
        placeholder="请输入搜索内容"
        class="input-with-select"
      >
        <template #append>
          <Icon @click="searchMap" icon="material-symbols:search" />
        </template>
      </el-input>
      <div class="search_result" v-if="searchResult.length > 0">
        <div class="search_box" v-for="item in searchResult" :key="item.hotPointID">
          <div class="search_title">
            <span class="search_name">{{ item.name }}</span>
            <span class="search_address">{{ item.address }}</span>
          </div>
          <div class="search_btn">
            <BaseButton type="primary" link size="small" @click="selectMap(item)">选择</BaseButton>
          </div>
        </div>
      </div>
    </div>
    <div class="pain_map" v-if="enablePolygon">
      <BaseButton type="primary" size="small" @click="drawingRange">绘制范围</BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, onBeforeUnmount, watch, nextTick } from 'vue'
import { ElInput } from 'element-plus'
import { useIcon } from '@/hooks/web/useIcon'
import { getCityLocation } from '@/utils/chinaCities'

const emit = defineEmits<{
  (e: 'marker-added', marker: () => void)
}>()

const { icon } = useIcon({ icon: 'material-symbols:search' })

// 定义组件属性
const props = defineProps({
  // 地图的中心点
  center: {
    type: Object,
    default: () => ({
      lng: getCityLocation(import.meta.env.VITE_CITY).lng,
      lat: getCityLocation(import.meta.env.VITE_CITY).lat
    })
  },
  // 地图的缩放级别
  zoom: {
    type: Number,
    default: 11
  },
  // 是否启用搜索功能
  enableSearch: {
    type: Boolean,
    default: true
  },
  // 是否启用右键标记功能
  enableRightClickMarker: {
    type: Boolean,
    default: true
  },
  // 是否开启绘制范围功能
  enablePolygon: {
    type: Boolean,
    default: true
  }
})

// 地图相关引用和变量
const mapContainer = ref<HTMLDivElement | null>(null)
let mapData: any = null
let T: any = null
let searchMapPoi: any = null
let polygonToolHandler: any = null

// 搜索相关数据
const searchText = ref('')
const searchResult = ref<any[]>([])
const searchSelectData = ref<any>({})

// 监听城市变化
watch(
  () => props.center,
  () => {
    if (mapData) {
      const centerPoint = new T.LngLat(props.center.lng, props.center.lat)
      mapData.centerAndZoom(centerPoint, props.zoom)
    }
  }
)
watch(
  () => props.enableRightClickMarker,
  () => {
    if (props.enableRightClickMarker) {
      enableRightClickMarkerFunction()
    } else {
      disableRightClickMarkerFunction()
    }
  }
  // { immediate: true }
)
// 地图初始化
const initMap = () => {
  if (!mapContainer.value) return

  // 确保天地图API已加载
  if (typeof window.T !== 'undefined') {
    T = window.T

    // 检查必要的API是否存在
    if (!checkApiAvailability()) {
      console.error('天地图API加载不完整')
      return
    }

    // 创建地图对象
    mapData = new T.Map(mapContainer.value, {
      style: TMAP_NORMAL_MAP
    })

    // 初始化地图中心点和缩放级别
    const centerPoint = new T.LngLat(props.center.lng, props.center.lat)
    mapData.centerAndZoom(centerPoint, props.zoom)
    mapData.disableScrollWheelZoom() // 禁止滚动缩放地图

    // 添加地图控件
    addMapControls()

    // 判断是否可以启用标记功能
    if (props.enableRightClickMarker) {
      // 延迟执行确保地图完全初始化
      setTimeout(() => {
        if (mapData) {
          enableRightClickMarkerFunction()
        }
      }, 100)
    }

    // const _CarTrack = new T.CarTrack(mapData, {
    //   interval: 5,
    //   speed: 10,
    //   dynamicLine: true,
    //   polylinestyle: { color: '#2C64A7', weight: 5, opacity: 0.9 },
    //   Datas: datas.features.map(function (obj, i) {
    //     const coordinates = obj.geometry.coordinates
    //     const lnlat = new T.LngLat(coordinates[0], coordinates[1])
    //     return lnlat
    //   })
    // })

    console.log('地图初始化成功')
  } else {
    // 如果API还未加载完成，等待一段时间后重试
    console.log('等待天地图API加载...')
    setTimeout(initMap, 500)
  }
}
// 检测当前地图中心点是否和传入地址一样
const getCenter = () => {
  setTimeout(() => {
    if (mapData.getCenter().lat !== props.center.lat) {
      const centerPoint = new T.LngLat(props.center.lng, props.center.lat)
      mapData.centerAndZoom(centerPoint, props.zoom)
    }
  }, 200)
}

// 检查API可用性
const checkApiAvailability = () => {
  return T.Map && T.LngLat && T.Marker
}

// 添加地图控件
const addMapControls = () => {
  if (T.Control && T.Control.Zoom) {
    mapData.addControl(new T.Control.Zoom())
  }
  // 确保地图不显示网格
  try {
    // 尝试设置地图样式，禁用网格
    if (mapData.setMapStyle) {
      mapData.setMapStyle({
        style: 'normal', // 使用标准样式
        features: ['road', 'building', 'water', 'land'], // 只显示这些要素，避免网格
        version: 8
      })
    }
  } catch (e) {
    console.warn('设置地图样式失败:', e)
  }
}

// 启用右键标记功能
const enableRightClickMarkerFunction = () => {
  if (!mapData || !T) {
    console.warn('右键标记功能API不完整')
    return
  }

  try {
    const obj = {
      text: '标记',
      isDisable: false,
      callback: function (lnglat) {
        if (props.enableRightClickMarker) {
          const marker = new T.Marker(new T.LngLat(lnglat.getLng(), lnglat.getLat()))
          mapData.clearOverLays()
          //向地图上添加标注
          mapData.addOverLay(marker)
          getMarkerAddressInfo(lnglat.getLng(), lnglat.getLat())
        }
      }
    }
    const menu = new T.ContextMenu({ width: 140 })
    const item = new T.MenuItem(obj.text, obj.callback)
    menu.addItem(item)
    mapData.addContextMenu(menu)
    mapData.clearOverLays()
  } catch (e) {
    console.error('右键标记功能初始化失败:', e)
  }
}
// 禁用右键标记功能
const disableRightClickMarkerFunction = () => {
  if (!mapData || !T) {
    console.warn('右键标记功能API不完整')
    return
  }

  // 安全检查：确保 removeContextMenu 方法存在
  if (mapData.removeContextMenu && typeof mapData.removeContextMenu === 'function') {
    try {
      mapData.removeContextMenu()
    } catch (e) {
      console.warn('移除右键菜单失败:', e)
    }
  } else {
    console.warn('removeContextMenu 方法不可用')
  }
}

// 新增函数：获取标记点地址信息
const getMarkerAddressInfo = (lng, lat) => {
  try {
    // 使用天地图逆地理编码服务
    const geocoder = new T.Geocoder()
    const point = new T.LngLat(lng, lat)

    geocoder.getLocation(point, (result) => {
      if (result && result.addressComponent) {
        console.log('逆地理编码结果:', result)
        const addressInfo = result.addressComponent
        const markerData = {
          lng: lng,
          lat: lat,
          address: addressInfo?.address || '未知地址',
          province: addressInfo?.province || '',
          county: addressInfo?.county || '',
          city: addressInfo?.city || ''
        }

        // 将完整的标记信息传递给父组件
        emit('marker-added', markerData)
      } else {
        // 如果无法获取地址信息，至少传递经纬度
        emit('marker-added', {
          lng: lng,
          lat: lat,
          address: '未知地址'
        })
      }
    })
  } catch (e) {
    console.error('获取地址信息失败:', e)
    // 出错时仍然传递基本的经纬度信息
    emit('marker-added', {
      lng: lng,
      lat: lat,
      address: '未知地址'
    })
  }
}

// 设置标记点回显lng--经度, lat--纬度
const setMarkerEcho = (lng, lat) => {
  if (!lng || !T) {
    console.warn('标记点回显API不完整')
    return
  }
  mapData.clearOverLays()
  //创建标注对象
  const marker = new T.Marker(new T.LngLat(lng, lat))
  //向地图上添加标注
  mapData.addOverLay(marker)
  const centerPoint = new T.LngLat(lng, lat)
  mapData.centerAndZoom(centerPoint, props.zoom)
}

// 绘制范围功能
const drawingRange = () => {
  if (!mapData || !T) {
    console.warn('绘制功能API不完整')
    return
  }

  const config = {
    showLabel: true,
    color: 'blue',
    weight: 3,
    opacity: 0.5,
    fillColor: '#409eff',
    fillOpacity: 0.5
  }

  try {
    mapData.clearOverLays()
    // 关闭之前的绘制工具
    if (polygonToolHandler) {
      polygonToolHandler.close()
    }

    // 创建新的绘制工具
    polygonToolHandler = new T.PolygonTool(mapData, config)
    polygonToolHandler.open()

    // 监听绘制完成事件
    polygonToolHandler.addEventListener('draw', (e) => {
      const resultArr = e.currentLnglats
      const drawArr = []
      resultArr.forEach((item: any) => {
        // 将覆盖物返回父级
        drawArr.push({ lng: item.lng, lat: item.lat })
      })
      // 可以在这里处理绘制完成后的数据
      console.log('绘制完成，坐标点:', drawArr)
    })
  } catch (e) {
    console.error('绘制功能初始化失败:', e)
  }
}

// 添加地图覆盖物示例
const addRange = (data) => {
  if (!mapData || !T) {
    console.warn('添加覆盖物功能API不完整')
    return
  }

  try {
    const lngLatArray = data.map((item) => new T.LngLat(item.lng, item.lat))
    const polygon = new T.Polygon(lngLatArray, { color: 'blue', weight: 2, opacity: 0.5 })
    mapData.addOverLay(polygon)
  } catch (e) {
    console.error('添加覆盖物失败:', e)
  }
}

// 地图搜索功能
const searchMap = () => {
  console.log('执行地图搜索功能')
  if (!mapData || !T) {
    console.warn('搜索功能API不完整')
    return
  }

  try {
    const config = {
      pageCapacity: 10,
      onSearchComplete: handleSearchResult
    }

    // 创建搜索对象
    searchMapPoi = new T.LocalSearch(mapData, config)
    searchMapPoi.search(searchText.value, 7)
  } catch (e) {
    console.error('搜索执行失败:', e)
  }
}

// 选择搜索结果
const selectMap = (item: any) => {
  try {
    const [lon, lat] = item.lonlat.split(',')
    const marker = new T.Marker(new T.LngLat(lon, lat))
    mapData.centerAndZoom(new T.LngLat(lon, lat), 14)
    mapData.clearOverLays()
    //向地图上添加标注
    mapData.addOverLay(marker)
    searchSelectData.value = item
    searchResult.value = []
  } catch (e) {
    console.error('选择地图位置失败:', e)
  }
}

// 处理搜索结果
const handleSearchResult = (result) => {
  try {
    searchResult.value = result.getPois()

    // 根据结果类型处理
    switch (parseInt(result.getResultType())) {
      case 1:
        // 解析点数据结果
        handlePoisResult(result.getPois())
        break
      case 2:
        // 解析推荐城市
        handleStatisticsResult(result.getStatistics())
        break
      case 3:
        // 解析行政区划边界
        handleAreaResult(result.getArea())
        break
      case 4:
        // 解析建议词信息
        handleSuggestsResult(result.getSuggests())
        break
    }
  } catch (e) {
    console.error('处理搜索结果失败:', e)
  }
}

// 处理点数据结果
const handlePoisResult = (poisData) => {
  if (!poisData || !mapData || !T) return

  try {
    // 显示搜索列表
    const divMarker = document.createElement('div')
    // 坐标数组，设置最佳比例尺时会用到
    const zoomArr = []

    for (let i = 0; i < poisData.length; i++) {
      // 闭包
      ;(function (i) {
        // 名称
        const name = poisData[i].name
        // 地址
        const address = poisData[i].address
        // 坐标
        const lnglatArr = poisData[i].lonlat.split(',')
        const lnglat = new T.LngLat(lnglatArr[0], lnglatArr[1])

        const winHtml = '名称:' + name + '<br/>地址:' + address

        // 创建标注对象
        const marker = new T.Marker(lnglat)
        // 地图上添加标注点
        mapData.addOverLay(marker)
        // 注册标注点的点击事件
        const markerInfoWin = new T.InfoWindow(winHtml, { autoPan: true })
        marker.addEventListener('click', function () {
          marker.openInfoWindow(markerInfoWin)
        })

        zoomArr.push(lnglat)

        // 在页面上显示搜索的列表
        const a = document.createElement('a')
        a.href = 'javascript://'
        a.innerHTML = name
        a.onclick = function () {
          showPosition(marker, winHtml)
        }
        divMarker.appendChild(document.createTextNode(i + 1 + '.'))
        divMarker.appendChild(a)
        divMarker.appendChild(document.createElement('br'))
      })(i)
    }

    // 显示地图的最佳级别
    if (zoomArr.length > 0) {
      mapData.setViewport(zoomArr)
    }

    // 显示搜索结果统计
    divMarker.appendChild(
      document.createTextNode(
        '共' +
          searchMapPoi.getCountNumber() +
          '条记录，分' +
          searchMapPoi.getCountPage() +
          '页,当前第' +
          searchMapPoi.getPageIndex() +
          '页'
      )
    )
  } catch (e) {
    console.error('处理点数据结果失败:', e)
  }
}

// 处理统计结果（推荐城市）
const handleStatisticsResult = (statisticsData) => {
  if (!statisticsData) return

  try {
    let priorityCitysHtml = ''
    let allAdminsHtml = ''
    const priorityCitys = statisticsData.priorityCitys

    if (priorityCitys) {
      // 推荐城市显示
      priorityCitysHtml += '在中国以下城市有结果<ul>'
      for (let i = 0; i < priorityCitys.length; i++) {
        priorityCitysHtml +=
          '<li>' + priorityCitys[i].name + '(' + priorityCitys[i].count + ')</li>'
      }
      priorityCitysHtml += '</ul>'
    }

    const allAdmins = statisticsData.allAdmins
    if (allAdmins) {
      allAdminsHtml += '更多城市<ul>'
      for (let i = 0; i < allAdmins.length; i++) {
        allAdminsHtml += '<li>' + allAdmins[i].name + '(' + allAdmins[i].count + ')'
        const childAdmins = allAdmins[i].childAdmins
        if (childAdmins) {
          for (let m = 0; m < childAdmins.length; m++) {
            allAdminsHtml +=
              '<blockquote>' + childAdmins[m].name + '(' + childAdmins[m].count + ')</blockquote>'
          }
        }
        allAdminsHtml += '</li>'
      }
      allAdminsHtml += '</ul>'
    }
  } catch (e) {
    console.error('处理统计结果失败:', e)
  }
}

// 处理行政区划边界结果
const handleAreaResult = (areaData) => {
  if (!areaData || !mapData || !T) return

  try {
    if (areaData.points) {
      // 坐标数组，设置最佳比例尺时会用到
      const pointsArr = []
      const points = areaData.points

      for (let i = 0; i < points.length; i++) {
        const regionLngLats = []
        const regionArr = points[i].region.split(',')

        for (let m = 0; m < regionArr.length; m++) {
          const lnglatArr = regionArr[m].split(' ')
          const lnglat = new T.LngLat(lnglatArr[0], lnglatArr[1])
          regionLngLats.push(lnglat)
          pointsArr.push(lnglat)
        }

        // 创建线对象
        const line = new T.Polyline(regionLngLats, {
          color: 'blue',
          weight: 3,
          opacity: 1,
          lineStyle: 'dashed'
        })
        // 向地图上添加线
        mapData.addOverLay(line)
      }

      // 显示最佳比例尺
      if (pointsArr.length > 0) {
        mapData.setViewport(pointsArr)
      }
    }

    if (areaData.lonlat) {
      const regionArr = areaData.lonlat.split(',')
      mapData.panTo(new T.LngLat(regionArr[0], regionArr[1]))
    }
  } catch (e) {
    console.error('处理行政区划边界结果失败:', e)
  }
}

// 处理建议词信息
const handleSuggestsResult = (suggestsData) => {
  if (!suggestsData) return

  try {
    // 建议词提示，如果搜索类型为公交规划建议词或公交站搜索时，返回结果为公交信息的建议词。
    let suggestsHtml = '建议词提示<ul>'
    for (let i = 0; i < suggestsData.length; i++) {
      suggestsHtml +=
        '<li>' +
        suggestsData[i].name +
        "&nbsp;&nbsp;<font style='color:#666666'>" +
        suggestsData[i].address +
        '</font></li>'
    }
    suggestsHtml += '</ul>'
  } catch (e) {
    console.error('处理建议词信息失败:', e)
  }
}

// 显示位置信息（搜索结果列表中的点击事件）
const showPosition = (marker, winHtml) => {
  if (marker && marker.openInfoWindow) {
    const markerInfoWin = new T.InfoWindow(winHtml, { autoPan: true })
    marker.openInfoWindow(markerInfoWin)
  }
}

// 清理地图所有资源
const clearMap = () => {
  setTimeout(() => {
    if (mapData) {
      // 先移除右键菜单
      if (mapData.removeContextMenu && typeof mapData.removeContextMenu === 'function') {
        try {
          mapData.removeContextMenu()
        } catch (e) {
          console.warn('清理右键菜单失败:', e)
        }
      }
      // 清理覆盖物
      mapData.clearOverLays()
    }
  }, 300)
}
// 卸载地图
const unmountMap = () => {
  if (mapData) {
    console.log('清理地图覆盖物')
    try {
      // 清理地图覆盖物
      if (mapData.clearOverlays && typeof mapData.clearOverlays === 'function') {
        mapData.clearOverlays()
        console.log('清理地图覆盖物1111')
      }

      // 销毁地图
      if (mapData.destroy && typeof mapData.destroy === 'function') {
        mapData.destroy()
        console.log('清理地图覆盖物222')
      }
    } catch (e) {
      console.warn('销毁地图时出错:', e)
    }
  }
}

// 清理地图标点
const clearMarkers = () => {
  if (mapData) {
    try {
      mapData.clearOverLays()
    } catch (e) {
      console.warn('清理地图标点时出错:', e)
    }
  }
}

// 组件挂载时初始化地图
onMounted(() => {
  // 等待DOM渲染完成后再初始化地图
  setTimeout(() => {
    initMap()
  }, 200)
})

// 组件卸载前清理
onBeforeUnmount(() => {
  if (mapData) {
    try {
      // 清理地图覆盖物
      if (mapData.clearOverlays && typeof mapData.clearOverlays === 'function') {
        mapData.clearOverlays()
      }

      // 关闭绘制工具
      if (polygonToolHandler) {
        polygonToolHandler.close()
      }
    } catch (e) {
      console.warn('清理地图资源时出错:', e)
    }

    // 清理引用
    mapData = null
    T = null
    searchMapPoi = null
    polygonToolHandler = null
  }
})

// 暴露方法给父组件
defineExpose({
  drawingRange,
  addRange,
  setMarkerEcho,
  clearMap,
  unmountMap,
  getCenter,
  clearMarkers
})
</script>

<style scoped lang="less">
.tmap {
  position: relative;
}

.tian-di-tu-map {
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.search {
  position: absolute;
  top: 10px;
  left: 65px;
  z-index: 999;
}

.search_result {
  height: 285px;
  max-width: 300px;
  overflow: auto;
  background-color: #fff;

  .search_box {
    display: flex;
    padding: 5px;
    border-bottom: 1px solid gainsboro;
    justify-content: space-between;
    align-items: center;
  }

  .search_name {
    display: block;
    font-size: 14px;
    font-weight: bold;
  }

  .search_address {
    font-size: 12px;
    color: #666;
  }
}

.pain_map {
  position: absolute;
  top: 10px;
  right: 30px;
  z-index: 999;
}
</style>
